import { useEffect, useState } from 'react';
import { useChat } from '../../meeting/hooks/useChat';
import { BaseChat, BaseChatProps } from './BaseChat';
import { useSelector } from '../../../reduxToolkit/typedRedux';
import { sendMeetingMessage } from '../../../services/services';
import { usePermission } from '../../../hooks/usePermission';
import { MeetingChatMessage } from '../../../interfaces/interfaces';
import { useRouter } from 'next/router';
import { differenceInMinutes } from 'date-fns';
import { UserReportModal } from '../UserReportModal';

export interface MeetingChatProps extends BaseChatProps {
  meetingId: string;
}

export const MeetingChat = ({ meetingId }: MeetingChatProps) => {
  const meeting = useSelector((state) => state.video.meeting);
  const event = useSelector((state) => state.common.event);
  const user = useSelector((state) => state.common.user);
  const isBannedFromChat = useSelector((state) => state.chat.isBannedFromChat);
  const datetimeJoined = useSelector((state) => state.video.datetimeJoined);

  const { loadMessages, checkIfUserIsBannedFromChat, setOnChatReload, setOnMeetingChat, setOnMessageDeleted, canChat, setOnChatHighlight } = useChat(meetingId);
  const { isEventHost } = usePermission();
  const router = useRouter();

  const [messages, setMessages] = useState<MeetingChatMessage[] | undefined>([]);

  const minutesSinceJoined = differenceInMinutes(new Date(), datetimeJoined!) + 1;

  async function sendMessage(message: string, replyTo?: MeetingChatMessage) {
    try {
      await sendMeetingMessage(message, event?._id!, meetingId, replyTo?._id);
    } catch (err) {
      console.error(err);
    }
  }

  async function fetchMessages() {
    const messages = await loadMessages(undefined, meetingId, meeting?.meetingtype === 'meeting' ? minutesSinceJoined : undefined);
    setMessages(messages);
    checkIfUserIsBannedFromChat();
  }

  useEffect(() => {
    setOnChatReload({
      action: () => {
        setMessages([]);
      },
    });
    setOnMeetingChat({
      action: (data: { msg: MeetingChatMessage }) => setMessages((previous) => [...previous!, { ...data.msg, isSeen: false }]),
    });
    setOnMessageDeleted({
      action: (data: { messageId: string }) => {
        setMessages((previous) => previous?.filter((message) => message._id !== data.messageId));
      },
    });
    setOnChatHighlight({
      action: (data: { msg: MeetingChatMessage }) => setMessages((previous) => previous?.map((message) => (message._id === data.msg._id ? data.msg : message))),
    });
    fetchMessages();
  }, []);

  if (!meeting) return null;
  return (
    <>
      <BaseChat
        messages={messages}
        fetchMessages={fetchMessages}
        onSendMessage={sendMessage}
        canSend={!!(!isBannedFromChat && user)}
        isChatLocked={!canChat}
        chatExportId={meetingId}
        inputStyle={{ icon: 'text-slate-500' }}
        showChatLock
        showClearChat
        showExternalLink={meeting.meetingtype === 'presentation'}
        showHighlightsControl
        isAdmin={isEventHost(user, event)}
      />
      <UserReportModal />
    </>
  );
};
